import React, { useEffect, useState } from 'react';
import { submissionAPI } from '../utils/api';
import { fmtTimeRange } from '../utils/format';

// The round a save of the interview modal would email: a round being added,
// else the one being rescheduled, else the one whose "Send invite" was used.
export const busyTargetOf = (sm) => {
  if (!sm) return null;
  if (sm.addingRound && sm.newRound?.date) return { ...sm.newRound, round: (sm.interviewRounds || []).length + 1 };
  const rounds = sm.interviewRounds || [];
  if (sm.reschedulingIdx != null && rounds[sm.reschedulingIdx]) return rounds[sm.reschedulingIdx];
  if (sm.sendInviteOrigIdx != null) return rounds.find(r => r._origIdx === sm.sendInviteOrigIdx) || null;
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// InterviewerBusyWarning — warns when an interviewer is already booked for
// another interview that overlaps the slot being scheduled (any recruiter's
// booking counts). Advisory only: the modal asks for confirmation before
// sending, it does not block. Reports what it found through onChange.
//
// The check is debounced while the recruiter types, and a failed check simply
// shows nothing — it must never get in the way of scheduling.
// ─────────────────────────────────────────────────────────────────────────────
export default function InterviewerBusyWarning({
  emails, date, time, endTime, submissionId, roundId, roundNumber, isDark, onChange,
}) {
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    let live = true;
    const report = (list) => { if (!live) return; setConflicts(list); onChange?.(list); };
    const list = String(emails || '').split(',').map(s => s.trim()).filter(Boolean);
    if (!list.length || !date || !time) { report([]); return () => { live = false; }; }

    const timer = setTimeout(() => {
      submissionAPI.interviewerBusy({
        emails: list.join(','), date, time,
        endTime:             endTime || undefined,
        excludeSubmissionId: submissionId,
        // The round being rescheduled must not clash with itself.
        excludeRoundId:      roundId || undefined,
        excludeRound:        roundId ? undefined : roundNumber,
      })
        .then(({ data }) => report(data.conflicts || []))
        .catch(() => report([]));
    }, 400);
    return () => { live = false; clearTimeout(timer); };
  }, [emails, date, time, endTime, submissionId, roundId, roundNumber]); // eslint-disable-line

  if (!conflicts.length) return null;

  return (
    <div role="alert" style={{
      marginTop: 8, padding: '10px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.5,
      background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb',
      border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fcd34d'}`,
      color: isDark ? '#fcd34d' : '#92400e',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        ⚠ {conflicts.length === 1 ? 'Interviewer is' : 'Interviewers are'} already busy at this time
      </div>
      {conflicts.map((c, i) => (
        <div key={`${c.email}-${c.time}-${i}`}>
          <strong>{c.email}</strong> — in a meeting {fmtTimeRange(c.time, c.endTime)}
          {c.candidateName
            ? <> · R{c.round}{c.roundTitle ? ` ${c.roundTitle}` : ''} with {c.candidateName}{c.clientName ? ` (${c.clientName})` : ''}</>
            : <> · another interview</>}
        </div>
      ))}
      <div style={{ marginTop: 4, opacity: 0.85 }}>
        Pick another time or interviewer — sending anyway double-books them.
      </div>
    </div>
  );
}
